import React, { useMemo, useState } from "react";
import { Search, ChevronLeft, BookMarked, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { MODULE_KEYS } from "@/lib/adminModules";
import { MODULE_ICONS } from "@/lib/moduleIcons";
import { cn } from "@/lib/utils";
import {
  GUIDE_MODULE_META,
  GUIDE_MODULES_BY_KEY,
  resolveViewerRole,
  type GuideSection,
} from "@/content/guide";

const NEW_BADGE_WINDOW_DAYS = 14;
const VIEWED_KEY_PREFIX = "guide-viewed-";

const isRecentlyUpdated = (dateStr: string): boolean => {
  const days = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= NEW_BADGE_WINDOW_DAYS;
};

const getLastViewed = (moduleKey: string): number => {
  try {
    const raw = localStorage.getItem(VIEWED_KEY_PREFIX + moduleKey);
    return raw ? Number(raw) : 0;
  } catch {
    return 0;
  }
};

const markViewed = (moduleKey: string) => {
  try {
    localStorage.setItem(VIEWED_KEY_PREFIX + moduleKey, String(Date.now()));
  } catch {
    // localStorage unavailable (private mode, etc.) — the "new" badge just won't clear
  }
};

const NewBadge: React.FC = () => (
  <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
    New
  </span>
);

// Never a per-admin grant — same isSuperAdmin special-case the sidebar uses,
// rather than hasModule.
const SUPER_ADMIN_ONLY_KEYS: string[] = [MODULE_KEYS.ADMIN_MANAGEMENT, MODULE_KEYS.AI_GUIDELINES];

const Guide: React.FC = () => {
  const { isSuperAdmin, isCsm, hasModule } = useAuth();
  const viewerRole = resolveViewerRole(isSuperAdmin, isCsm);
  const [search, setSearch] = useState("");
  const [selectedModule, setSelectedModule] = useState<string | null>(MODULE_KEYS.DASHBOARD);
  const [, setViewTick] = useState(0);

  // Admin Management and AI Guidelines follow the same isSuperAdmin
  // special-case the sidebar uses, rather than hasModule — neither is ever a
  // per-admin grant.
  const accessibleModules = useMemo(
    () =>
      GUIDE_MODULE_META.filter((m) =>
        SUPER_ADMIN_ONLY_KEYS.includes(m.key) ? isSuperAdmin : hasModule(m.key),
      ),
    [isSuperAdmin, hasModule],
  );

  const visibleSections = (moduleKey: string): GuideSection[] => {
    const guide = GUIDE_MODULES_BY_KEY[moduleKey];
    if (!guide) return [];
    return guide.sections.filter((s) => !s.roles || s.roles.includes(viewerRole));
  };

  const hasNewContent = (moduleKey: string): boolean => {
    const lastViewed = getLastViewed(moduleKey);
    return visibleSections(moduleKey).some(
      (s) => isRecentlyUpdated(s.lastUpdated) && new Date(s.lastUpdated).getTime() > lastViewed,
    );
  };

  const query = search.trim().toLowerCase();
  const filteredModules = useMemo(() => {
    if (!query) return accessibleModules;
    return accessibleModules.filter((m) => {
      const guide = GUIDE_MODULES_BY_KEY[m.key];
      if (!guide) return false;
      if (m.label.toLowerCase().includes(query)) return true;
      if (guide.overview.toLowerCase().includes(query)) return true;
      return visibleSections(m.key).some(
        (s) => s.title.toLowerCase().includes(query) || s.body.toLowerCase().includes(query),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleModules, query, viewerRole]);

  const handleSelect = (moduleKey: string) => {
    setSelectedModule(moduleKey);
    markViewed(moduleKey);
    setViewTick((n) => n + 1);
  };

  const selectedGuide = selectedModule ? GUIDE_MODULES_BY_KEY[selectedModule] : null;
  const selectedMeta = selectedModule
    ? accessibleModules.find((m) => m.key === selectedModule)
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookMarked className="h-6 w-6 text-primary" />
          Manual
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          What each module does, and what you specifically can do in it.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden md:flex md:min-h-[32rem]">
        {/* Module list — always visible on desktop; on mobile, hidden once a module is open. */}
        <div className={cn("md:w-72 md:shrink-0 md:border-r md:border-gray-200", selectedModule && "hidden md:block")}>
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search the manual..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <ul className="divide-y divide-gray-100 md:max-h-[28rem] overflow-y-auto">
            {filteredModules.map((m) => {
              const Icon = MODULE_ICONS[m.key] ?? HelpCircle;
              const isActive = selectedModule === m.key;
              return (
                <li key={m.key}>
                  <button
                    onClick={() => handleSelect(m.key)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors",
                      isActive && "bg-primary/5 md:border-r-2 md:border-primary -mr-px",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-primary" : "text-gray-400")} />
                    <span className={cn("flex-1 truncate", isActive ? "font-medium text-gray-900" : "text-gray-700")}>
                      {m.label}
                    </span>
                    {hasNewContent(m.key) && <NewBadge />}
                  </button>
                </li>
              );
            })}
            {filteredModules.length === 0 && (
              <li className="px-4 py-6 text-sm text-gray-500 text-center">No matches.</li>
            )}
          </ul>
        </div>

        {/* Content — hidden on mobile until a module is selected; always visible on desktop. */}
        <div className={cn("flex-1 p-6", !selectedModule && "hidden md:block")}>
          {selectedGuide && selectedMeta ? (
            <>
              <button
                onClick={() => setSelectedModule(null)}
                className="md:hidden mb-4 inline-flex items-center gap-1 text-sm text-primary"
              >
                <ChevronLeft className="h-4 w-4" /> All modules
              </button>
              <h2 className="text-lg font-semibold text-gray-900">{selectedMeta.label}</h2>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{selectedGuide.overview}</p>
              <div className="mt-6 space-y-5">
                {visibleSections(selectedGuide.moduleKey).map((section) => (
                  <div key={section.id}>
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      {section.title}
                      {isRecentlyUpdated(section.lastUpdated) && <NewBadge />}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-line leading-relaxed">
                      {section.body}
                    </p>
                  </div>
                ))}
                {visibleSections(selectedGuide.moduleKey).length === 0 && (
                  <p className="text-sm text-gray-500">Nothing specific to your role here yet.</p>
                )}
              </div>
            </>
          ) : (
            <div className="hidden md:flex h-full items-center justify-center text-sm text-gray-400">
              Select a module from the list to see its manual.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Guide;
